import React, { useState, useEffect } from "react";
import ClientAppSidebar from "../../../../../components/ClientAppSidebar";
import { Button } from "../../../../../ui/components/Button";
import { Table } from "../../../../../ui/components/Table";
import { Badge } from "../../../../../ui/components/Badge";
import { DropdownMenu } from "../../../../../ui/components/DropdownMenu";
import { ToggleGroup } from "../../../../../ui/components/ToggleGroup";
import * as SubframeCore from "@subframe/core";
import {
  FeatherAlertCircle,
  FeatherCheckCircle,
  FeatherChevronDown,
  FeatherDownload,
  FeatherCalendar,
  FeatherFileText,
  FeatherMessageCircle,
  FeatherPercent,
  FeatherBriefcase,
  FeatherCreditCard,
  FeatherClock,
  FeatherTrendingUp,
} from "@subframe/core";
import {
  getPendingInputs,
  getFilingStatus,
  getUpcomingDeadlines,
  getRecentFilings,
  downloadAllProofs,
  downloadBlob,
  PendingInputsParams,
  UpcomingDeadlinesParams,
  RecentFilingsParams,
  PendingInputDTO,
  FilingStatusSummaryDTO,
  UpcomingDeadlineDTO,
  RecentFilingDTO,
} from "../../data";
import QueryUploadModal from "../components/QueryUploadModal";

// Helper to format date for display (e.g., "15 Dec, 14:30")
const formatDateForDisplay = (dateString: string | null): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day} ${month}, ${hours}:${minutes}`;
  } catch {
    return dateString;
  }
};

// Helper to format date as "DD MMM YYYY" (e.g., "7 Dec 2024")
const formatDateShort = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return dateString;
  }
};

// Helper to format compliance type by replacing underscores with spaces
const formatComplianceType = (complianceType: string): string => {
  return complianceType.replace(/_/g, " ");
};

// Helper to get icon for compliance type
const getComplianceIcon = (complianceType: string) => {
  const type = complianceType.toLowerCase();
  if (type.includes("gst")) return <FeatherFileText />;
  if (type.includes("tds")) return <FeatherPercent />;
  if (type.includes("roc")) return <FeatherBriefcase />;
  if (type.includes("income") || type.includes("tax"))
    return <FeatherCreditCard />;
  return <FeatherFileText />;
};

const ClientComplianceDashboard: React.FC = () => {
  // Dashboard data
  const [pendingInputs, setPendingInputs] = useState<PendingInputDTO[]>([]);
  const [filingStatus, setFilingStatus] = useState<FilingStatusSummaryDTO[]>(
    [],
  );
  const [upcomingDeadlines, setUpcomingDeadlines] = useState<
    UpcomingDeadlineDTO[]
  >([]);
  const [recentFilings, setRecentFilings] = useState<RecentFilingDTO[]>([]);

  // Pagination states - all start at 0, then increment after initial load
  const [pendingInputsOffset, setPendingInputsOffset] = useState(0);
  const [upcomingDeadlinesOffset, setUpcomingDeadlinesOffset] = useState(0);
  const [recentFilingsOffset, setRecentFilingsOffset] = useState(0);

  // Pagination metadata
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingInputsPagination, setPendingInputsPagination] = useState({
    total: 0,
    limit: 5,
    offset: 0,
    has_more: false,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [upcomingDeadlinesPagination, setUpcomingDeadlinesPagination] =
    useState({
      total: 0,
      limit: 5,
      offset: 0,
      has_more: false,
    });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [recentFilingsPagination, setRecentFilingsPagination] = useState({
    total: 0,
    limit: 5,
    offset: 0,
    has_more: false,
  });

  // Filter states
  const [deadlinesFilter, setDeadlinesFilter] = useState<string>("8eb346bf"); // "all"
  const [filingsType, setFilingsType] = useState<string>("all");

  // Loading states for individual sections - initialize to true for initial load
  const [loadingPendingInputs, setLoadingPendingInputs] = useState(true);
  const [loadingFilingStatus, setLoadingFilingStatus] = useState(true);
  const [loadingDeadlines, setLoadingDeadlines] = useState(true);
  const [loadingFilings, setLoadingFilings] = useState(true);

  // Error states for individual sections
  const [errorPendingInputs, setErrorPendingInputs] = useState<string | null>(
    null,
  );
  const [errorFilingStatus, setErrorFilingStatus] = useState<string | null>(
    null,
  );
  const [errorDeadlines, setErrorDeadlines] = useState<string | null>(null);
  const [errorFilings, setErrorFilings] = useState<string | null>(null);

  // Query upload modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
  const [selectedQueryId, setSelectedQueryId] = useState<number | null>(null);
  const [selectedQueryTitle, setSelectedQueryTitle] = useState<string>("");

  // Map ToggleGroup values to filter values
  const getDeadlinesFilterValue = (
    toggleValue: string,
  ): "all" | "this_week" | "this_month" | "next_month" | "overdue" => {
    switch (toggleValue) {
      case "8eb346bf":
        return "all";
      case "7d3b241c":
        return "this_week";
      case "4f73aa47":
        return "this_month";
      case "241bc834":
        return "next_month";
      case "92431512":
        return "overdue";
      default:
        return "all";
    }
  };

  // Initial load - use Promise.all to load all endpoints in parallel
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Set all loading states to true
        setLoadingPendingInputs(true);
        setLoadingFilingStatus(true);
        setLoadingDeadlines(true);
        setLoadingFilings(true);

        // Clear any previous errors
        setErrorPendingInputs(null);
        setErrorFilingStatus(null);
        setErrorDeadlines(null);
        setErrorFilings(null);

        // Load all endpoints in parallel
        const [pendingInputsRes, filingStatusRes, deadlinesRes, filingsRes] =
          await Promise.all([
            getPendingInputs({ limit: 5, offset: 0 }),
            getFilingStatus(),
            getUpcomingDeadlines({ filter: "all", limit: 5, offset: 0 }),
            getRecentFilings({ limit: 5, offset: 0 }),
          ]);

        // Set all data at once
        setPendingInputs(pendingInputsRes.data.data);
        setPendingInputsPagination(pendingInputsRes.data.pagination);

        // FilingStatusResponseDTO.data is directly the array, not wrapped
        setFilingStatus(filingStatusRes.data);

        setUpcomingDeadlines(deadlinesRes.data.data);
        setUpcomingDeadlinesPagination(deadlinesRes.data.pagination);

        setRecentFilings(filingsRes.data.data);
        setRecentFilingsPagination(filingsRes.data.pagination);

        // Set offsets for next page (when user clicks "Load More")
        setPendingInputsOffset(5);
        setUpcomingDeadlinesOffset(5);
        setRecentFilingsOffset(5);
      } catch (err: any) {
        console.error("Initial load error:", err);
        const errorMessage =
          err.message || "Failed to load data. Please try again later.";
        // Set errors for all sections if initial load fails
        setErrorPendingInputs(errorMessage);
        setErrorFilingStatus(errorMessage);
        setErrorDeadlines(errorMessage);
        setErrorFilings(errorMessage);
      } finally {
        // Set all loading states to false
        setLoadingPendingInputs(false);
        setLoadingFilingStatus(false);
        setLoadingDeadlines(false);
        setLoadingFilings(false);
      }
    };

    loadInitialData();
  }, []); // Only run on mount

  // Handle filter changes - reset and load fresh data
  useEffect(() => {
    // Skip initial mount (filter hasn't changed yet)
    if (upcomingDeadlinesOffset === 0 && upcomingDeadlines.length === 0) {
      return;
    }
    loadUpcomingDeadlines(true); // true = reset offset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadlinesFilter]);

  useEffect(() => {
    // Skip initial mount (filter hasn't changed yet)
    if (recentFilingsOffset === 0 && recentFilings.length === 0) {
      return;
    }
    loadRecentFilings(true); // true = reset offset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filingsType]);

  // Handle pagination (Load More buttons)
  const handleLoadMorePendingInputs = () => {
    loadPendingInputs();
  };

  const handleLoadMoreDeadlines = () => {
    loadUpcomingDeadlines(false); // false = append (don't reset)
  };

  const handleLoadMoreFilings = () => {
    loadRecentFilings(false); // false = append (don't reset)
  };

  // Load more pending inputs (pagination)
  const loadPendingInputs = async () => {
    try {
      setLoadingPendingInputs(true);
      setErrorPendingInputs(null);
      const params: PendingInputsParams = {
        limit: 5,
        offset: pendingInputsOffset,
      };
      const response = await getPendingInputs(params);

      setPendingInputs([...pendingInputs, ...response.data.data]);
      setPendingInputsPagination(response.data.pagination);
      setPendingInputsOffset((prev) => prev + 5);
    } catch (err: any) {
      console.error("Pending inputs error:", err);
      setErrorPendingInputs(err.message || "Failed to load pending inputs.");
    } finally {
      setLoadingPendingInputs(false);
    }
  };

  // Load upcoming deadlines (pagination or filter change)
  const loadUpcomingDeadlines = async (resetOffset: boolean = false) => {
    try {
      setLoadingDeadlines(true);
      setErrorDeadlines(null);
      const filterValue = getDeadlinesFilterValue(deadlinesFilter);
      const offset = resetOffset ? 0 : upcomingDeadlinesOffset;

      const params: UpcomingDeadlinesParams = {
        filter: filterValue,
        limit: 5,
        offset: offset,
      };
      const response = await getUpcomingDeadlines(params);

      if (resetOffset || offset === 0) {
        setUpcomingDeadlines(response.data.data);
        setUpcomingDeadlinesOffset(5); // Set for next load
      } else {
        setUpcomingDeadlines([...upcomingDeadlines, ...response.data.data]);
        setUpcomingDeadlinesOffset((prev) => prev + 5);
      }
      setUpcomingDeadlinesPagination(response.data.pagination);
    } catch (err: any) {
      console.error("Upcoming deadlines error:", err);
      setErrorDeadlines(err.message || "Failed to load upcoming deadlines.");
    } finally {
      setLoadingDeadlines(false);
    }
  };

  // Load recent filings (pagination or filter change)
  const loadRecentFilings = async (resetOffset: boolean = false) => {
    try {
      setLoadingFilings(true);
      setErrorFilings(null);
      const offset = resetOffset ? 0 : recentFilingsOffset;

      const params: RecentFilingsParams = {
        compliance_type: filingsType !== "all" ? filingsType : undefined,
        limit: 5,
        offset: offset,
      };
      const response = await getRecentFilings(params);

      if (resetOffset || offset === 0) {
        setRecentFilings(response.data.data);
        setRecentFilingsOffset(5); // Set for next load
      } else {
        setRecentFilings([...recentFilings, ...response.data.data]);
        setRecentFilingsOffset((prev) => prev + 5);
      }
      setRecentFilingsPagination(response.data.pagination);
    } catch (err: any) {
      console.error("Recent filings error:", err);
      setErrorFilings(err.message || "Failed to load recent filings.");
    } finally {
      setLoadingFilings(false);
    }
  };

  const handleDownloadProofs = async (complianceId: number) => {
    try {
      const blob = await downloadAllProofs(complianceId);
      const filename = `compliance_${complianceId}_proofs.zip`;
      downloadBlob(blob, filename);
    } catch (err: any) {
      console.error("Download proofs error:", err);
      alert("Failed to download proofs. Please try again.");
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { label: string; variant: string; tooltip: string }
    > = {
      filed: {
        label: "Filed",
        variant: "success",
        tooltip: "Filed: Submitted successfully",
      },
      pending: {
        label: "Pending",
        variant: "warning",
        tooltip: "Pending: Awaiting submission",
      },
      overdue: {
        label: "Overdue",
        variant: "error",
        tooltip: "Overdue: Past due date",
      },
      up_to_date: {
        label: "Up to date",
        variant: "success",
        tooltip: "Up to date: All filings current",
      },
      on_track: {
        label: "On track",
        variant: "brand",
        tooltip: "On track: Meeting schedule",
      },
    };
    const config = statusConfig[status] || {
      label: status,
      variant: "info",
      tooltip: status,
    };
    return config;
  };

  // Generate insights from data (simplified version)
  const getInsights = () => {
    const insights = [];
    const today = new Date();
    const tenDaysFromNow = new Date(today.getTime() + 10 * 24 * 60 * 60 * 1000);

    // Count filings due soon
    const dueSoonCount = upcomingDeadlines.filter((d) => {
      const dueDate = new Date(d.due_date);
      return (
        dueDate >= today &&
        dueDate <= tenDaysFromNow &&
        d.status_label === "Upcoming"
      );
    }).length;

    // Count overdue
    const overdueCount = upcomingDeadlines.filter(
      (d) => d.status_label === "Overdue",
    ).length;

    if (dueSoonCount > 0) {
      insights.push({
        type: "warning",
        icon: <FeatherAlertCircle />,
        title: `${dueSoonCount} filing${dueSoonCount > 1 ? "s" : ""} due soon`,
        description: "Upcoming deadlines in the next 10 days",
      });
    }

    if (overdueCount === 0) {
      insights.push({
        type: "success",
        icon: <FeatherCheckCircle />,
        title: "All caught up",
        description: "No overdue compliance items right now",
      });
    }

    // Add generic insights (these would come from backend in real implementation)
    insights.push({
      type: "brand",
      icon: <FeatherTrendingUp />,
      title: "GST liability increased",
      description: "Higher than last month due to increased revenue",
    });

    insights.push({
      type: "warning",
      icon: <FeatherClock />,
      title: "TDS return due soon",
      description: "Review deductee details before filing",
    });

    return insights.slice(0, 4); // Limit to 4 insights
  };

  // Skeleton loader for pending inputs table
  const renderPendingInputsSkeleton = () => {
    const skeletonStyle: React.CSSProperties = {
      backgroundImage:
        "linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(229,231,235,0.8) 50%, rgba(255,255,255,0.4) 100%)",
      backgroundSize: "200% 100%",
      animation: "skeletonFade 1.6s ease-in-out infinite",
    };

    return (
      <>
        <style>{`
          @keyframes skeletonFade {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        `}</style>
        <div className="flex w-full flex-col items-start">
          <div className="flex w-full items-center gap-4 border-b border-solid border-neutral-border px-4 py-3">
            <div
              className="h-4 w-32 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-48 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-24 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-24 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-20 rounded bg-neutral-100 ml-auto"
              style={skeletonStyle}
            />
          </div>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="flex w-full items-center gap-4 border-b border-solid border-neutral-border px-4 py-3"
            >
              <div
                className="h-4 w-32 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-4 w-48 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-4 w-24 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-4 w-24 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-8 w-24 rounded bg-neutral-100 ml-auto"
                style={skeletonStyle}
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  // Skeleton loader for filing status cards
  const renderFilingStatusSkeleton = () => {
    const skeletonStyle: React.CSSProperties = {
      backgroundImage:
        "linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(229,231,235,0.8) 50%, rgba(255,255,255,0.4) 100%)",
      backgroundSize: "200% 100%",
      animation: "skeletonFade 1.6s ease-in-out infinite",
    };

    return (
      <>
        <style>{`
          @keyframes skeletonFade {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        `}</style>
        <div className="flex w-full items-start gap-3.5">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm"
            >
              <div
                className="h-6 w-32 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-5 w-24 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-4 w-40 rounded bg-neutral-100"
                style={skeletonStyle}
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  // Skeleton loader for deadlines table
  const renderDeadlinesSkeleton = () => {
    const skeletonStyle: React.CSSProperties = {
      backgroundImage:
        "linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(229,231,235,0.8) 50%, rgba(255,255,255,0.4) 100%)",
      backgroundSize: "200% 100%",
      animation: "skeletonFade 1.6s ease-in-out infinite",
    };

    return (
      <>
        <style>{`
          @keyframes skeletonFade {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        `}</style>
        <div className="flex w-full flex-col items-start">
          <div className="flex w-full items-center gap-4 border-b border-solid border-neutral-border px-4 py-3">
            <div
              className="h-4 w-24 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-32 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-24 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-20 rounded bg-neutral-100 ml-auto"
              style={skeletonStyle}
            />
          </div>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="flex w-full items-center gap-4 border-b border-solid border-neutral-border px-4 py-3"
            >
              <div
                className="h-4 w-24 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-4 w-32 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-4 w-24 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-6 w-20 rounded bg-neutral-100 ml-auto"
                style={skeletonStyle}
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  // Skeleton loader for filings table
  const renderFilingsSkeleton = () => {
    const skeletonStyle: React.CSSProperties = {
      backgroundImage:
        "linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(229,231,235,0.8) 50%, rgba(255,255,255,0.4) 100%)",
      backgroundSize: "200% 100%",
      animation: "skeletonFade 1.6s ease-in-out infinite",
    };

    return (
      <>
        <style>{`
          @keyframes skeletonFade {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        `}</style>
        <div className="flex w-full flex-col items-start">
          <div className="flex w-full items-center gap-4 border-b border-solid border-neutral-border px-4 py-3">
            <div
              className="h-4 w-32 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-24 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-20 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-24 rounded bg-neutral-100"
              style={skeletonStyle}
            />
            <div
              className="h-4 w-24 rounded bg-neutral-100 ml-auto"
              style={skeletonStyle}
            />
          </div>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="flex w-full items-center gap-4 border-b border-solid border-neutral-border px-4 py-3"
            >
              <div
                className="h-4 w-32 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-4 w-24 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-6 w-20 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-4 w-24 rounded bg-neutral-100"
                style={skeletonStyle}
              />
              <div
                className="h-4 w-16 rounded bg-neutral-100 ml-auto"
                style={skeletonStyle}
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const insights = getInsights();
  const statusBadgeConfigs = filingStatus.map((status) => ({
    ...status,
    badge: getStatusBadge(status.status),
  }));

  return (
    <div className="flex h-screen w-full">
      <ClientAppSidebar />
      <div className="container max-w-none flex w-full grow shrink-0 basis-0 flex-col items-start gap-10 bg-neutral-50 py-12 overflow-auto">
        {/* Header */}
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-col items-start gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Compliance Dashboard
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Manage your compliance requirements and deadlines
            </span>
          </div>
          {/* <Button
            variant="brand-secondary"
            icon={<FeatherMessageCircle />}
            onClick={() => {}}
          >
            Contact your Zournal Team
          </Button> */}
        </div>

        {/* Pending Inputs from Client */}
        <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Pending Inputs from Client
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Information required from you to complete upcoming filings.
              </span>
            </div>
          </div>
          {loadingPendingInputs ? (
            renderPendingInputsSkeleton()
          ) : errorPendingInputs ? (
            <div className="flex w-full items-center gap-2 rounded-lg bg-error-50 px-4 py-3">
              <FeatherAlertCircle className="text-body font-body text-error-600" />
              <span className="text-body font-body text-error-700">
                {errorPendingInputs}
              </span>
              <Button
                variant="neutral-secondary"
                size="small"
                onClick={() => {
                  setLoadingPendingInputs(true);
                  setErrorPendingInputs(null);
                  getPendingInputs({ limit: 5, offset: 0 })
                    .then((res) => {
                      setPendingInputs(res.data.data);
                      setPendingInputsPagination(res.data.pagination);
                      setPendingInputsOffset(5);
                    })
                    .catch((err) => {
                      setErrorPendingInputs(
                        err.message || "Failed to load pending inputs.",
                      );
                    })
                    .finally(() => {
                      setLoadingPendingInputs(false);
                    });
                }}
              >
                Retry
              </Button>
            </div>
          ) : pendingInputs.length === 0 ? (
            <div className="flex w-full flex-col items-center justify-center py-12">
              <FeatherCheckCircle
                className="text-subtext-color mb-2"
                style={{ width: 48, height: 48 }}
              />
              <span className="text-body font-body text-subtext-color">
                No pending inputs
              </span>
            </div>
          ) : (
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Compliance Type</Table.HeaderCell>
                  <Table.HeaderCell>Required Input</Table.HeaderCell>
                  <Table.HeaderCell>Requested On</Table.HeaderCell>
                  <Table.HeaderCell>Requested By</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.HeaderRow>
              }
            >
              {pendingInputs.map((input) => (
                <Table.Row key={input.id}>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                      {formatComplianceType(input.compliance_type)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body font-body text-default-font">
                      {input.required_input}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body font-body text-subtext-color">
                      {formatDateShort(input.requested_on)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body font-body text-default-font">
                      {input.requested_by.name}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex grow shrink-0 basis-0 items-center justify-end">
                      <Button
                        variant="brand-secondary"
                        size="small"
                        onClick={() => {
                          setSelectedQueryId(input.query_id);
                          setSelectedQueryTitle(input.required_input);
                          setIsUploadModalOpen(true);
                        }}
                      >
                        Open Query
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          )}
        </div>

        {/* Filing Status Summary and Insights */}
        <div className="flex w-full items-start gap-8">
          {/* Filing Status Summary - Full Width */}
          <div className="flex flex-col items-start gap-4 flex-shrink-0 w-full">
            <div className="flex w-full items-center justify-between">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Filing Status Summary
              </span>
            </div>
            {loadingFilingStatus ? (
              renderFilingStatusSkeleton()
            ) : errorFilingStatus ? (
              <div className="flex w-full items-center gap-2 rounded-lg bg-error-50 px-4 py-3">
                <FeatherAlertCircle className="text-body font-body text-error-600" />
                <span className="text-body font-body text-error-700">
                  {errorFilingStatus}
                </span>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  onClick={() => {
                    setLoadingFilingStatus(true);
                    setErrorFilingStatus(null);
                    getFilingStatus()
                      .then((res) => {
                        setFilingStatus(res.data);
                      })
                      .catch((err) => {
                        setErrorFilingStatus(
                          err.message || "Failed to load filing status.",
                        );
                      })
                      .finally(() => {
                        setLoadingFilingStatus(false);
                      });
                  }}
                >
                  Retry
                </Button>
              </div>
            ) : filingStatus.length === 0 ? (
              <div className="flex w-full flex-col items-center justify-center py-12 rounded-md border border-solid border-neutral-border bg-default-background">
                <FeatherFileText
                  className="text-subtext-color mb-2"
                  style={{ width: 48, height: 48 }}
                />
                <span className="text-body font-body text-subtext-color">
                  No filing status available
                </span>
                <span className="text-caption font-caption text-subtext-color mt-1">
                  Filing status will appear here once compliance entries are
                  available
                </span>
              </div>
            ) : (
              <>
                <div className="flex w-full items-start gap-3.5">
                  {statusBadgeConfigs.slice(0, 2).map((status, index) => (
                    <div
                      key={index}
                      className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm cursor-pointer"
                    >
                      <div className="flex w-full items-center justify-between">
                        <span className="text-heading-3 font-heading-3 text-default-font">
                          {status.compliance_name}
                        </span>
                        <div className="text-body font-body text-subtext-color">
                          {getComplianceIcon(status.compliance_type)}
                        </div>
                      </div>
                      <div className="flex w-full flex-col items-start gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-body-bold font-body-bold text-subtext-color">
                            Status:
                          </span>
                          <Badge variant={status.badge.variant as any}>
                            {status.badge.label}
                          </Badge>
                        </div>
                        <span className="text-body font-body text-subtext-color">
                          Next due:{" "}
                          {status.next_due_date
                            ? formatDateShort(status.next_due_date)
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {statusBadgeConfigs.length > 2 && (
                  <div className="flex w-full items-start gap-3.5">
                    {statusBadgeConfigs.slice(2, 4).map((status, index) => (
                      <div
                        key={index + 2}
                        className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm cursor-pointer"
                      >
                        <div className="flex w-full items-center justify-between">
                          <span className="text-heading-3 font-heading-3 text-default-font">
                            {status.compliance_name}
                          </span>
                          <div className="text-body font-body text-subtext-color">
                            {getComplianceIcon(status.compliance_type)}
                          </div>
                        </div>
                        <div className="flex w-full flex-col items-start gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-body-bold font-body-bold text-subtext-color">
                              Status:
                            </span>
                            <Badge variant={status.badge.variant as any}>
                              {status.badge.label}
                            </Badge>
                          </div>
                          <span className="text-body font-body text-subtext-color">
                            Next due:{" "}
                            {status.next_due_date
                              ? formatDateShort(status.next_due_date)
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Compliance Insights & Alerts - Right Side (2/5) */}
          {/* <div className="flex flex-col items-start gap-4 self-stretch flex-shrink-0 w-2/5">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Compliance Insights &amp; Alerts
            </span>
            <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
              <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-3">
                {insights.map((insight, index) => (
                  <div
                    key={index}
                    className="flex w-full items-start gap-3 rounded-lg px-2 py-2"
                  >
                    <div
                      className={`text-body font-body mt-0.5 ${
                        insight.type === "success"
                          ? "text-success-600"
                          : insight.type === "warning"
                          ? "text-warning-600"
                          : insight.type === "error"
                          ? "text-error-600"
                          : "text-brand-600"
                      }`}
                    >
                      {insight.icon}
                    </div>
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        {insight.title}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        {insight.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div> */}
        </div>

        {/* Upcoming Deadlines */}
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Upcoming Deadlines
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Key tax and compliance deadlines for the next few weeks.
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <ToggleGroup
              value={deadlinesFilter}
              onValueChange={(value: string) => {
                setDeadlinesFilter(value);
              }}
            >
              <ToggleGroup.Item icon={null} value="8eb346bf">
                All
              </ToggleGroup.Item>
              <ToggleGroup.Item icon={null} value="7d3b241c">
                This Week
              </ToggleGroup.Item>
              <ToggleGroup.Item icon={null} value="4f73aa47">
                This Month
              </ToggleGroup.Item>
              <ToggleGroup.Item icon={null} value="241bc834">
                Next Month
              </ToggleGroup.Item>
              <ToggleGroup.Item icon={null} value="92431512">
                Overdue
              </ToggleGroup.Item>
            </ToggleGroup>
            {loadingDeadlines ? (
              renderDeadlinesSkeleton()
            ) : errorDeadlines ? (
              <div className="flex w-full items-center gap-2 rounded-lg bg-error-50 px-4 py-3">
                <FeatherAlertCircle className="text-body font-body text-error-600" />
                <span className="text-body font-body text-error-700">
                  {errorDeadlines}
                </span>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  onClick={() => {
                    const filterValue =
                      getDeadlinesFilterValue(deadlinesFilter);
                    setLoadingDeadlines(true);
                    setErrorDeadlines(null);
                    getUpcomingDeadlines({
                      filter: filterValue,
                      limit: 5,
                      offset: 0,
                    })
                      .then((res) => {
                        setUpcomingDeadlines(res.data.data);
                        setUpcomingDeadlinesPagination(res.data.pagination);
                        setUpcomingDeadlinesOffset(5);
                      })
                      .catch((err) => {
                        setErrorDeadlines(
                          err.message || "Failed to load upcoming deadlines.",
                        );
                      })
                      .finally(() => {
                        setLoadingDeadlines(false);
                      });
                  }}
                >
                  Retry
                </Button>
              </div>
            ) : upcomingDeadlines.length === 0 ? (
              <div className="flex w-full flex-col items-center justify-center py-12">
                <FeatherCalendar
                  className="text-subtext-color mb-2"
                  style={{ width: 48, height: 48 }}
                />
                <span className="text-body font-body text-subtext-color">
                  No upcoming deadlines
                </span>
              </div>
            ) : (
              <>
                <Table
                  header={
                    <Table.HeaderRow>
                      <Table.HeaderCell>Due Date</Table.HeaderCell>
                      <Table.HeaderCell>Compliance Type</Table.HeaderCell>
                      <Table.HeaderCell>Period</Table.HeaderCell>
                      <Table.HeaderCell>Status</Table.HeaderCell>
                    </Table.HeaderRow>
                  }
                >
                  {upcomingDeadlines.map((deadline) => (
                    <Table.Row key={deadline.id}>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {formatDateShort(deadline.due_date)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="truncate text-body font-body text-default-font max-w-[200px]">
                          {deadline.compliance_form_name ||
                            deadline.compliance_type}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-subtext-color">
                          {deadline.period}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              deadline.status_label === "Overdue"
                                ? "error"
                                : deadline.status_label === "Filed"
                                  ? "success"
                                  : "warning"
                            }
                          >
                            {deadline.status_label}
                          </Badge>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table>
                <div className="flex w-full items-start">
                  {/* <div className="flex items-center gap-2 cursor-pointer">
                    <span className="text-body-bold font-body-bold text-brand-600">
                      View all compliance deadlines
                    </span>
                    <FeatherArrowRight className="text-body font-body text-brand-600" />
                  </div> */}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Recent Filings */}
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Recent Filings
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Summary of recent filings across all compliance types
              </span>
            </div>
            <div className="flex items-center gap-2">
              <SubframeCore.DropdownMenu.Root>
                <SubframeCore.DropdownMenu.Trigger asChild={true}>
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    iconRight={<FeatherChevronDown />}
                  >
                    {filingsType === "all" ? "All Types" : filingsType}
                  </Button>
                </SubframeCore.DropdownMenu.Trigger>
                <SubframeCore.DropdownMenu.Portal>
                  <SubframeCore.DropdownMenu.Content
                    side="bottom"
                    align="end"
                    sideOffset={4}
                    asChild={true}
                  >
                    <DropdownMenu>
                      <DropdownMenu.DropdownItem
                        icon={null}
                        onClick={() => {
                          setFilingsType("all");
                        }}
                      >
                        All
                      </DropdownMenu.DropdownItem>
                      <DropdownMenu.DropdownItem
                        icon={null}
                        onClick={() => {
                          setFilingsType("GST");
                        }}
                      >
                        GST
                      </DropdownMenu.DropdownItem>
                      <DropdownMenu.DropdownItem
                        icon={null}
                        onClick={() => {
                          setFilingsType("TDS");
                        }}
                      >
                        TDS
                      </DropdownMenu.DropdownItem>
                      <DropdownMenu.DropdownItem
                        icon={null}
                        onClick={() => {
                          setFilingsType("PF");
                        }}
                      >
                        PF
                      </DropdownMenu.DropdownItem>
                      {/* <DropdownMenu.DropdownItem
                        icon={null}
                        onClick={() => {
                          setFilingsType("Income Tax");
                        }}
                      >
                        Income Tax
                      </DropdownMenu.DropdownItem> */}
                    </DropdownMenu>
                  </SubframeCore.DropdownMenu.Content>
                </SubframeCore.DropdownMenu.Portal>
              </SubframeCore.DropdownMenu.Root>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            {loadingFilings ? (
              renderFilingsSkeleton()
            ) : errorFilings ? (
              <div className="flex w-full items-center gap-2 rounded-lg bg-error-50 px-4 py-3">
                <FeatherAlertCircle className="text-body font-body text-error-600" />
                <span className="text-body font-body text-error-700">
                  {errorFilings}
                </span>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  onClick={() => {
                    setLoadingFilings(true);
                    setErrorFilings(null);
                    const params: RecentFilingsParams = {
                      compliance_type:
                        filingsType !== "all" ? filingsType : undefined,
                      limit: 5,
                      offset: 0,
                    };
                    getRecentFilings(params)
                      .then((res) => {
                        setRecentFilings(res.data.data);
                        setRecentFilingsPagination(res.data.pagination);
                        setRecentFilingsOffset(5);
                      })
                      .catch((err) => {
                        setErrorFilings(
                          err.message || "Failed to load recent filings.",
                        );
                      })
                      .finally(() => {
                        setLoadingFilings(false);
                      });
                  }}
                >
                  Retry
                </Button>
              </div>
            ) : recentFilings.length === 0 ? (
              <div className="flex w-full flex-col items-center justify-center py-12">
                <FeatherFileText
                  className="text-subtext-color mb-2"
                  style={{ width: 48, height: 48 }}
                />
                <span className="text-body font-body text-subtext-color">
                  No recent filings
                </span>
              </div>
            ) : (
              <>
                <Table
                  header={
                    <Table.HeaderRow>
                      <Table.HeaderCell>Compliance</Table.HeaderCell>
                      <Table.HeaderCell>Period</Table.HeaderCell>
                      <Table.HeaderCell>Status</Table.HeaderCell>
                      <Table.HeaderCell>Filed On</Table.HeaderCell>
                      <Table.HeaderCell>Attachments</Table.HeaderCell>
                    </Table.HeaderRow>
                  }
                >
                  {recentFilings.map((filing) => (
                    <Table.Row key={filing.id}>
                      <Table.Cell>
                        <span className="truncate text-body-bold font-body-bold text-default-font max-w-[200px]">
                          {filing.compliance_form_name ||
                            filing.compliance_type}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-subtext-color">
                          {filing.period}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {(() => {
                          const badgeConfig = getStatusBadge(filing.status);
                          return (
                            <Badge variant={badgeConfig.variant as any}>
                              {badgeConfig.label}
                            </Badge>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {filing.filed_on
                            ? formatDateShort(filing.filed_on)
                            : "-"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {filing.has_proofs ? (
                          <div
                            className="flex items-center gap-2 cursor-pointer"
                            onClick={() => handleDownloadProofs(filing.id)}
                          >
                            <FeatherDownload className="text-body font-body text-brand-600" />
                            <span className="text-body font-body text-brand-600">
                              PDF
                            </span>
                          </div>
                        ) : (
                          <span className="text-body font-body text-subtext-color">
                            -
                          </span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table>
                <div className="flex w-full items-start">
                  {/* <div className="flex items-center gap-2 cursor-pointer">
                    <span className="text-body-bold font-body-bold text-brand-600">
                      View full filing history
                    </span>
                    <FeatherArrowRight className="text-body font-body text-brand-600" />
                  </div> */}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <QueryUploadModal
        open={isUploadModalOpen}
        onOpenChange={(open) => {
          setIsUploadModalOpen(open);
          if (!open) {
            setSelectedQueryId(null);
            setSelectedQueryTitle("");
          }
        }}
        queryId={selectedQueryId || 0}
        queryTitle={selectedQueryTitle}
      />
    </div>
  );
};

export default ClientComplianceDashboard;
